import { LeadDetails } from "lib/hooks/api/leads/useLead";
import type { CrmContactFields, CrmPerson } from "./SendToParagonCrmModal";

type LeadContact = NonNullable<NonNullable<LeadDetails>["contacts"]>[number];

/** What every query that returns a contact carries, however each one types its nulls. */
type ContactFieldSource = {
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  jobTitle?: string | null;
  jobFunction?: string[] | null;
  phone?: string | null;
  email?: string | null;
  seniority?: string | null;
  buyer?: { name?: string | null } | null;
};

/**
 * A contact's details as a CRM mapping reaches them. Shared by the two surfaces that send a
 * contact's details — its own button, and the person a lead is sent as — so neither drifts from
 * the other on what a mapped field is filled with.
 */
export function crmContactFieldsFrom(contact: ContactFieldSource): CrmContactFields {
  return {
    salutation: contact.title ?? undefined,
    firstName: contact.firstName ?? undefined,
    lastName: contact.lastName ?? undefined,
    jobTitle: contact.jobTitle ?? undefined,
    // Joined because a mapping can only point this at a text column, and a contact's functions are
    // a set with no primary one. Sending one would drop the rest on whichever sorted first.
    jobFunction: contact.jobFunction?.join(", "),
    phone: contact.phone ?? undefined,
    email: contact.email ?? undefined,
    seniority: contact.seniority ?? undefined,
    buyerName: contact.buyer?.name ?? undefined,
  };
}

/**
 * A lead's contact as the send form names and seeds a person from.
 *
 * `name` only labels the choice, so it falls back rather than going empty: a contact with neither
 * name would otherwise render a blank option and have the form say it was being sent as nobody.
 */
export function crmPersonFrom(contact: LeadContact): CrmPerson {
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ");

  return {
    ...crmContactFieldsFrom(contact),
    id: contact.id,
    name: fullName || contact.email || "Unnamed contact",
  };
}
